import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { billingFetch } from "./billingApi";
import "./SidebarCredits.css";

const ACTIVE_SUBSCRIPTION_STATUSES = ["active", "cancelling"];

function toCreditNumber(value) {
  const credits = Number(value);
  return Number.isFinite(credits) ? credits : 0;
}

function getOpenMeterBalance(data) {
  const openmeter = data?.openmeter ?? {};
  return {
    balance: toCreditNumber(openmeter.balance ?? data?.balance),
    usage: toCreditNumber(openmeter.usage ?? data?.usage),
    hasAccess: Boolean(openmeter.hasAccess ?? data?.hasAccess),
  };
}

export default function SidebarCredits({ userId, isDarkMode }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(0);
  const [hasAccess, setHasAccess] = useState(false);
  const [totalGranted, setTotalGranted] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!userId) return;

    try {
      const [syncData, subData] = await Promise.allSettled([
        billingFetch("/stripe/sync-usage"),
        supabase
          .from("subscriptions")
          .select("credits_total, credits_used, status")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      let bal = 0;
      let use = 0;
      let access = false;
      let sub = null;

      if (syncData.status === "fulfilled") {
        const openMeterBalance = getOpenMeterBalance(syncData.value);
        bal = openMeterBalance.balance;
        use = openMeterBalance.usage;
        access = openMeterBalance.hasAccess;
      }

      if (subData.status === "fulfilled" && subData.value.data) {
        sub = subData.value.data;
      }

      const hasActiveSub = Boolean(
        sub && ACTIVE_SUBSCRIPTION_STATUSES.includes(sub.status)
      );

      // Fallback: if OpenMeter balance is missing/empty but Supabase shows
      // an active subscription, use the subscription credit fields.
      if (bal <= 0 && hasActiveSub) {
        const fallback =
          toCreditNumber(sub.credits_total) - toCreditNumber(sub.credits_used);
        if (fallback > 0) {
          bal = fallback;
          use = toCreditNumber(sub.credits_used);
        }
      }

      access = access || hasActiveSub || bal > 0;

      setBalance(bal);
      setUsage(use);
      setHasAccess(access);
      setTotalGranted(bal + use > 0 ? bal + use : 0);
    } catch {
      // silent
    } finally {
      setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchBalance();
    const interval = setInterval(fetchBalance, 15000);

    const onRefresh = () => fetchBalance();
    window.addEventListener("zoomey-credits-refresh", onRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener("zoomey-credits-refresh", onRefresh);
    };
  }, [userId, fetchBalance]);

  if (!loaded) return null;

  const goToBilling = () =>
    navigate("/userprofile", { state: { activeMenu: 15 } });

  // No plan active - show "Get Started" state
  const noPlan = !hasAccess && balance <= 0 && usage <= 0;
  if (noPlan) {
    return (
      <div className={`sidebar-credits ${isDarkMode ? "dark" : ""}`}>
        <div className="sc-header">
          <span className="sc-title">Zoomey Credits</span>
          <button className="sc-add-btn" onClick={goToBilling}>
            Get Started
          </button>
        </div>
        <div className="sc-no-plan">No active plan</div>
      </div>
    );
  }

  const total = totalGranted;
  const percent = total > 0 ? Math.max(0, Math.min(100, (balance / total) * 100)) : 0;
  const barColor = percent > 30 ? "#0f9b58" : percent > 10 ? "#f59e0b" : "#ef4444";

  return (
    <div className={`sidebar-credits ${isDarkMode ? "dark" : ""}`}>
      <div className="sc-header">
        <span className="sc-title">Zoomey Credits</span>
        <button className="sc-add-btn" onClick={goToBilling}>
          Add Credits
        </button>
      </div>
      <div className="sc-bar-bg">
        <div
          className="sc-bar-fill"
          style={{ width: `${percent}%`, backgroundColor: barColor }}
        />
      </div>
      <div className="sc-footer">
        <span className="sc-remaining">
          {Math.round(balance)}/{Math.round(total)} remaining
        </span>
        <span className="sc-days">{Math.round(usage)} used</span>
      </div>
    </div>
  );
}
